/* global importName */
import Vue from 'vue'
import Router from 'vue-router'

import {
  authGuard,
  importGuard,
  navGuard
} from './helpers'
import { getDeepProperty } from '@/helpers/deepObject.js'
import store from '@/services/store'
import * as types from '@/services/store/modules/account/mutation-types.js'

import accountsRoutes from './accounts'

const Layout = () => importName('@/components/Layout.vue', 'mainLayout')
const Dashboard = () => importName('@/scenes/Overview/Dashboard.vue', 'dashboard')
const NotFound = () => importName('@/scenes/NotFound.vue', 'notFound')

Vue.use(Router)

const router = new Router({
  meta: {
    preventNav: false
  },
  mode: 'history',
  routes: [
    {
      path: '/',
      component: Layout,
      children: [
        {
          path: '',
          name: 'overview',
          component: Dashboard,
          meta: {
            breadcrumbs: [
              { title: 'Show Overview' }
            ]
          }
        },
        ...accountsRoutes,
        {
          path: '/logout',
          name: 'logout',
          beforeEnter: (to, from, next) => {
            window.location = `${process.env.AUTH_BASE_PATH}/logout`
          }
        },
        {
          path: '*',
          name: 'not-found',
          component: NotFound
        }
      ]
    }
  ]
})

// Don't know if we need this, but leaving it here so we know we can
// router.afterEach(async (to, from) => {
//   console.log('afterEach')
// })

router.beforeEach(async (to, from, next) => {
  // isLoggedIn isn't always true because you could be requesting
  // a route that we want to surface to a logged out user
  const isLoggedIn = await authGuard(to, from)

  if (isLoggedIn) {
    // For every route change we will want to make sure we load everything that
    // should be globally accessible and should be available via the URL.
    // I may want to move specific ones (like episode, campaign, etc.) into
    // the beforeEach of each route
    await store.dispatch('initializeApp', {to, from})

    const user = store.getters.user

    // this had been before-enter for overview, but it wasn't getting called din favor of this beforeEach
    if (to.name === 'overview') {
      let activePodcast = store.getters.activePodcast
      // Podcast loading will always be handled by the time we get here, so it either exists or doesn't
      // and we can redirect appropriately to the proper page
      if (getDeepProperty(activePodcast, 'id')) {
        next({
          name: 'show-overview',
          params: {
            podcastId: getDeepProperty(activePodcast, 'id'),
            accountId: getDeepProperty(activePodcast, 'account.id')
          }
        })
        return
      } else {
        next({name: 'new-show'})
        return
      }
    }

    // this won't work in beforeEnter because that doesn't check param changes
    if (to.name === 'ad-creative') {
      let activePodcast = store.getters.activePodcast
      // Podcast loading will always be handled by the time we get here, so it either exists or doesn't
      // and we can redirect appropriately to the proper page
      if (!getDeepProperty(activePodcast, 'ad_creatives')) {
        next({
          name: 'show-overview',
          params: {
            podcastId: getDeepProperty(activePodcast, 'id'),
            accountId: getDeepProperty(activePodcast, 'account.id')
          }
        })
        return
      }
    }

    // This is where we need to redirect off to accounts
    // Only logout should be allowed to not include /accounts in the route
    if (!/^\/accounts/.test(to.path) && !/^logout/.test(to.name)) {
      let account = null
      if (to.params.accountId) {
        account = user.accounts.collection.find((a) => a.id === to.params.accountId)
      } else if (getDeepProperty(user, 'podcasts.default.account')) {
        account = getDeepProperty(user, 'podcasts.default.account')
      } else {
        // Defaults to first account in the collection
        account = user.accounts.collection[0]
      }
      store.commit(types.SET_CURRENT_ACCOUNT, account)
      next(`/accounts/${account.id}${to.path}`)
      return
    }

    // If switching from an /accounts/ url and the id changes we need to update the currentAccount object
    if (getDeepProperty(store, 'getters.currentAccount.id') && getDeepProperty(store, 'getters.currentAccount.id') !== to.params.accountId) {
      store.commit(types.SET_CURRENT_ACCOUNT, user.accounts.collection.find((a) => a.id === to.params.accountId))
    }
  }

  const nextRoute = await importGuard(to, from)

  if (nextRoute !== false) {
    next(nextRoute)
  } else {
    next(navGuard(router, to, from))
  }
})

export default router
